import React, { useState } from 'react';
import {
    Table,
    Text,
    Select,
    TextInput,
    Box,
    Group,
    Title,
    Flex,
    ScrollArea,
    Grid, Button
} from '@mantine/core';
import {RentContract, RentContractType} from '../../../models/RentContract';
import { CreateRentContract } from './CreateRentContract';
import { RentContractDocument } from './RentContractDocument';
import {EditRentContract} from "./EditRentContract";

interface RentContractsForOrgProps {
    orgId: string;
    contracts: RentContract[];
    onCreatedRentContract: () => void;
}

type ContractFilter = 'all' | 'active' | 'future' | 'past';

export const RentContractsForOrg: React.FC<RentContractsForOrgProps> = ({
                                                                            orgId,
                                                                            contracts,
                                                                            onCreatedRentContract,
                                                                        }) => {
    const [filter, setFilter] = useState<ContractFilter>('all');
    const [searchText, setSearchText] = useState('');

    // Filter by active/future/past
    const now = new Date();
    const filteredContracts = contracts.filter((c) => {
        const start = new Date(c.startDate);
        const end = new Date(c.endDate);

        const isActive = now >= start && now <= end;
        const isFuture = now < start;
        const isPast = now > end;

        if (filter === 'active') return isActive;
        if (filter === 'future') return isFuture;
        if (filter === 'past') return isPast;
        return true; // 'all'
    });

    // Search
    const lowerSearch = searchText.toLowerCase();
    const displayedContracts = filteredContracts.filter((c) => {
        const fields = [
            c.id.toString(),
            c.storageLocation.name,
            c.firstName,
            c.lastName,
            c.email,
            c.phoneNumber,
            new Date(c.startDate).toLocaleDateString('de-DE'),
            new Date(c.endDate).toLocaleDateString('de-DE'),
            c.type,
            c.description,
            c.monthlyPrice.toString(),
        ];
        return fields.join(' ').toLowerCase().includes(lowerSearch);
    });

    if (displayedContracts.length === 0) {
        return (
            <>
                <Flex direction="column" justify="space-between" wrap="wrap" mb="xs">
                    <Flex direction={"row"} justify={"space-between"} align="center" gap="md" wrap="wrap">
                        <Title order={4}>Mietverträge</Title>

                        <CreateRentContract
                            organisationId={orgId}
                            onCreatedRentContract={onCreatedRentContract}
                        />
                    </Flex>
                    <FilterControls
                        filter={filter}
                        setFilter={setFilter}
                        searchText={searchText}
                        setSearchText={setSearchText}
                    />
                </Flex>
                <Text>keine Verträge stimmen mit den Suchparametern überein</Text>
            </>
        );
    }

    return (
        <>
            <Flex direction="column" justify="space-between" wrap="wrap" mb="xs">
                <Flex direction={"row"} justify={"space-between"} align="center" gap="md" wrap="wrap">
                    <Title order={4}>Mietverträge</Title>

                    <CreateRentContract
                        organisationId={orgId}
                        onCreatedRentContract={onCreatedRentContract}
                    />
                </Flex>
                <FilterControls
                    filter={filter}
                    setFilter={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                />
            </Flex>

            <ScrollArea.Autosize mah={400}>
                <Table highlightOnHover >
                    <Table.Thead>
                    <Table.Tr>
                        <Table.Th>ID</Table.Th>
                        <Table.Th>Dokumente</Table.Th>
                        <Table.Th>Stellplatz</Table.Th>
                        <Table.Th>Name Mieter</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Telefonnummer</Table.Th>
                        <Table.Th>Start - Ende</Table.Th>
                        <Table.Th>Typ</Table.Th>
                        <Table.Th>Monatlicher Preis</Table.Th>
                        <Table.Th>Beschreibung</Table.Th>
                        <Table.Th></Table.Th>
                    </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                    {displayedContracts.map((contract) => (
                        <Table.Tr key={contract.id}>
                            <Table.Td>{contract.id}</Table.Td>
                            <Table.Td>
                                <RentContractDocument
                                    contract={contract}
                                    onCreatedRentContractDocument={onCreatedRentContract}
                                />
                            </Table.Td>
                            <Table.Td>{contract.storageLocation.name}</Table.Td>
                            <Table.Td>
                                {contract.firstName} {contract.lastName}
                            </Table.Td>
                            <Table.Td>{contract.email}</Table.Td>
                            <Table.Td>{contract.phoneNumber}</Table.Td>
                            <Table.Td>
                                {new Date(contract.startDate).toLocaleDateString('de-DE')} –{" "}
                                {new Date(contract.endDate).toLocaleDateString('de-DE')}
                            </Table.Td>
                            <Table.Td>{contract.type === RentContractType.MONTHLY ? "Monatlich" : "Jährlich"}</Table.Td>
                            <Table.Td>{contract.monthlyPrice}</Table.Td>
                            <Table.Td>{contract.description}</Table.Td>
                            <Table.Td><EditRentContract rentContractId={contract.id} organisationId={orgId} onClose={() => {}} onUpdated={onCreatedRentContract} /></Table.Td>

                        </Table.Tr>
                    ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea.Autosize>
        </>
    );
};

interface FilterControlsProps {
    filter: ContractFilter;
    setFilter: (val: ContractFilter) => void;
    searchText: string;
    setSearchText: (val: string) => void;
}

export function FilterControls({
                            filter,
                            setFilter,
                            searchText,
                            setSearchText,
                        }: FilterControlsProps) {
    return (
        <Box>
            <Group gap="sm">
                <Select
                    label="Filter"
                    data={[
                        { value: 'all', label: 'Alle' },
                        { value: 'active', label: 'Aktiv' },
                        { value: 'future', label: 'Zukunft' },
                        { value: 'past', label: 'Vergangenheit' },
                    ]}
                    value={filter}
                    onChange={(val) => setFilter(val as ContractFilter)}
                    size="xs"
                />
                <TextInput
                    label="Suche"
                    placeholder="Tippen um verträge zu durchsuchen"
                    value={searchText}
                    onChange={(e) => setSearchText(e.currentTarget.value)}
                    size="xs"
                />
            </Group>
        </Box>
    );
}
